import { useQuery } from '@apollo/client/react';
import { Field, FieldAttributes, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from 'common/components/FormField';
import {
    InputOptionGroupsDocument,
    IExtraCategoryValueFragment,
    IExtraCategoryOption,
    IExtraCategoryStringValue,
    IExtraCategoryChoiceValue,
} from 'graphql/types';
import { Typography } from 'common/components/Typography';
import { TInputOption } from 'common/types';

interface IProps extends FieldProps {
    label?: string;
    noGroupsOption?: boolean;
    // IDs to list first
    priorityIds?: string[];
    onChange?: (name: string, value: TInputOption[]) => void;
}

export const GroupSelectField = ({
    label,
    noGroupsOption,
    priorityIds,
    onChange,
    ...other
}: IProps & Partial<FieldAttributes<unknown>>) => {
    const [translate] = useTranslation();

    const { loading, data: groupsData } = useQuery(InputOptionGroupsDocument);

    let options = [];

    if (noGroupsOption) {
        options.push({
            id: 'noGroups',
            name: translate('noGroupsOption'),
            divider: true,
        });
    }

    type TGroupOption = {
        id: string;
        name: string;
        extraCategoryLabels: IExtraCategoryValueFragment[];
        divider?: boolean;
    };

    const allGroupOptions: TGroupOption[] = (groupsData?.groups || []).map(
        (group) => ({
            id: group.id,
            name: group.name,
            extraCategoryLabels: group.extraCategoryValues || [],
        })
    );

    let groupOptions = allGroupOptions;

    if (priorityIds && priorityIds.length > 0) {
        const prioritySet = new Set(priorityIds);
        const priorityOptions: TGroupOption[] = [];

        priorityIds.forEach((priorityId: string) => {
            const option = allGroupOptions.find(
                (group) => group.id === priorityId
            );

            if (option) priorityOptions.push(option);
        });

        const restOptions = allGroupOptions.filter(
            (group) => !prioritySet.has(group.id)
        );

        // Mark the last priority item with a divider so the dropdown visually
        // separates the prioritised items from the rest.
        if (priorityOptions.length && restOptions.length) {
            const lastIndex = priorityOptions.length - 1;
            priorityOptions[lastIndex] = {
                ...priorityOptions[lastIndex],
                divider: true,
            };
        }

        groupOptions = [...priorityOptions, ...restOptions];
    }

    options = [...options, ...groupOptions];

    const {
        form: { setFieldValue },
    } = other;

    const handleChange = (name: string, value: TInputOption[]) => {
        let newValue = [...value];

        // Select or deselect noGroupsOption based on other options
        // Either the noGroupsOption is selected or other options are selected, not both at the same time
        if (noGroupsOption) {
            const noGroupsOptionIndex = newValue.findIndex(
                (option) => option.id === 'noGroups'
            );

            if (noGroupsOptionIndex !== -1) {
                if (noGroupsOptionIndex === 0 && newValue.length > 1) {
                    newValue.splice(noGroupsOptionIndex, 1);
                } else {
                    newValue = newValue.filter(
                        (option) => option.id === 'noGroups'
                    );
                }
            }
        }

        onChange?.(name, newValue);
        setFieldValue(name, newValue);
    };

    return (
        <Field
            component={MultiSelect}
            getOptionDisplay={(option: TInputOption) => {
                if (option.id === 'noGroups') {
                    return <>{translate('noGroupsOption')}</>;
                }

                const extraCategoryString = (
                    option.extraCategoryLabels as IExtraCategoryValueFragment[]
                )
                    .filter(
                        ({ category }) =>
                            category.categoryType !==
                                IExtraCategoryOption.Boolean &&
                            category.categoryType !==
                                IExtraCategoryOption.Datetime &&
                            category.showLabel
                    )
                    .map((extraCategoryValue) => {
                        if (
                            extraCategoryValue.category.categoryType ===
                            IExtraCategoryOption.String
                        ) {
                            return (
                                extraCategoryValue as IExtraCategoryStringValue
                            ).stringValue;
                        }

                        return (extraCategoryValue as IExtraCategoryChoiceValue)
                            .choiceValue.stringValue;
                    })
                    .join(', ');

                return (
                    <>
                        {option.name}

                        {!!extraCategoryString && (
                            <Typography
                                noWrap
                                color="grey.500"
                                component="div"
                                sx={{
                                    mt: 0.5,
                                    minWidth: '0',
                                    width: '100%',
                                }}
                                variant="caption"
                            >
                                {extraCategoryString}
                            </Typography>
                        )}
                    </>
                );
            }}
            getOptionDivider={(option: TInputOption) => option.divider}
            getOptionKey={(option: TInputOption) => option.id}
            getOptionLabel={(option: TInputOption) => option.name}
            InputProps={{ label }}
            isOptionEqualToValue={(option: TInputOption, value: TInputOption) =>
                option.id === value.id
            }
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={translate('noOptionsText.groups')}
            options={options}
            onChange={handleChange}
            {...other}
        />
    );
};
